/**
 * Extensions Section Generator for Retrofit PDF Export
 * Handles extensions and conservatory data with proper photo pagination
 */

import type { RetrofitSurveyData } from '../utils/retrofitSurveyStorage';
import { generateHeader, generateFooter } from './retrofitPDFExportService';

/**
 * Optimize image for PDF rendering
 * Resizes and compresses images to prevent PDF corruption
 */
async function optimizeImageForPDF(
  imageUrl: string,
  maxWidth: number = 1600,
  maxHeight: number = 1200,
  quality: number = 0.90
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          const ratio = Math.min(widthRatio, heightRatio);
          
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        const optimizedUrl = canvas.toDataURL('image/webp', quality);
        resolve(optimizedUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}

/**
 * Generate Extensions Section HTML
 */
export function generateExtensionsSection(surveyData: RetrofitSurveyData): string {
  const { extensions } = surveyData;
  
  if (!extensions || (!extensions.existingExtensions?.length && !extensions.hasConservatory)) {
    console.log('⚠️ No extensions data to display');
    return '';
  }

  const existingExtensions = extensions.existingExtensions || [];
  const hasConservatory = extensions.hasConservatory ?? false;

  let html = `
    <div class="content-section">
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          <h3><span class="section-parent">EXTENSIONS & CONSERVATORY:</span> Property Details</h3>
        </div>
  `;

  // Add conservatory info
  if (hasConservatory) {
    html += `
      <div style="margin-bottom: 16px; page-break-inside: avoid;">
        <div style="background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); padding: 12px; border-radius: 6px; border: 2px solid #f59e0b; box-shadow: 0 2px 4px rgba(245, 158, 11, 0.1);">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
            <div style="width: 28px; height: 28px; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
              <svg style="width: 16px; height: 16px; color: white;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h4 style="margin: 0; font-size: 12px; font-weight: 700; color: #78350f; text-transform: uppercase; letter-spacing: 0.5px;">Conservatory</h4>
          </div>
          <div style="background: white; padding: 8px 10px; border-radius: 4px; border-left: 4px solid #f59e0b;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <svg style="width: 14px; height: 14px; color: #15803d; flex-shrink: 0;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9 12l2 2 4-4"></path>
              </svg>
              <span style="font-size: 10px; font-weight: 600; color: #15803d;">Conservatory Present</span>
            </div>
            <p style="margin: 6px 0 0 20px; font-size: 9px; color: #6b7280; line-height: 1.4;">
              Additional conservatory structure identified. Detailed photographs and measurements documented for assessment.
            </p>
          </div>
        </div>
      </div>
    `;
  }

  // Add extensions
  if (existingExtensions.length > 0) {
    existingExtensions.forEach((ext, index) => {
      const hasBasicInfo = ext.numberOfStoreys || ext.buildingAge || ext.wallConstructionType || ext.isWallInsulated !== undefined;
      const hasRoofInfo = ext.roofType;
      
      // Add page break after every 2 extensions (excluding conservatory)
      const shouldPageBreak = index > 0 && index % 2 === 0;
      
      html += `
        <div style="margin-bottom: 16px; page-break-inside: avoid; ${shouldPageBreak ? 'page-break-before: always;' : ''}">
          <div style="background: linear-gradient(to right, #f97316 0%, #ea580c 100%); padding: 10px 14px; border-radius: 6px 6px 0 0;">
            <h4 style="margin: 0; font-size: 13px; font-weight: 700; color: white; text-transform: uppercase; letter-spacing: 0.5px;">
              ${capitalizeWords(ext.type || `EXT ${index + 1}`)}
            </h4>
          </div>
          
          <div style="border: 2px solid #f97316; border-top: none; border-radius: 0 0 6px 6px; padding: 12px; background: white;">
            ${ext.area ? `
              <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); padding: 10px; border-radius: 6px; margin-bottom: 10px; border: 2px solid #3b82f6; box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);">
                <div style="display: flex; align-items: center; gap: 10px;">
                  <div style="width: 32px; height: 32px; background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); border-radius: 6px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <svg style="width: 18px; height: 18px; color: white;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                      <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                  </div>
                  <div>
                    <div style="font-size: 9px; font-weight: 600; color: #1e40af; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 2px;">Area (from Floor Plan)</div>
                    <div style="font-size: 14px; font-weight: 700; color: #1e3a8a;">${ext.area} m²</div>
                  </div>
                </div>
              </div>
            ` : ''}
            
            ${hasBasicInfo ? `
              <div style="background: #f9fafb; padding: 10px; border-radius: 6px; margin-bottom: 10px;">
                <div style="font-size: 9px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 2px solid #e5e7eb;">
                  Basic Information
                </div>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                  ${ext.numberOfStoreys ? `
                    <div style="background: white; padding: 6px 8px; border-radius: 4px; border-left: 3px solid #8b5cf6;">
                      <div style="font-size: 8px; font-weight: 600; color: #6b7280; margin-bottom: 2px;">Number of Storeys</div>
                      <div style="font-size: 11px; font-weight: 600; color: #1f2937;">${ext.numberOfStoreys}</div>
                    </div>
                  ` : ''}
                  
                  ${ext.buildingAge ? `
                    <div style="background: white; padding: 6px 8px; border-radius: 4px; border-left: 3px solid #8b5cf6;">
                      <div style="font-size: 8px; font-weight: 600; color: #6b7280; margin-bottom: 2px;">Building Age</div>
                      <div style="font-size: 11px; font-weight: 600; color: #1f2937;">${capitalizeWords(ext.buildingAge || '')}</div>
                    </div>
                  ` : ''}
                  
                  ${ext.wallConstructionType ? `
                    <div style="background: white; padding: 6px 8px; border-radius: 4px; border-left: 3px solid #10b981;">
                      <div style="font-size: 8px; font-weight: 600; color: #6b7280; margin-bottom: 2px;">Wall Construction Type</div>
                      <div style="font-size: 11px; font-weight: 600; color: #1f2937;">${formatWallType(ext.wallConstructionType || '')}</div>
                    </div>
                  ` : ''}
                  
                  ${(ext as any).wallThicknessMeasurement ? `
                    <div style="background: white; padding: 6px 8px; border-radius: 4px; border-left: 3px solid #3b82f6;">
                      <div style="font-size: 8px; font-weight: 600; color: #6b7280; margin-bottom: 2px;">Wall Thickness Measurement</div>
                      <div style="font-size: 11px; font-weight: 600; color: #1f2937;">${(ext as any).wallThicknessMeasurement}mm</div>
                    </div>
                  ` : ''}
                  
                  ${ext.isWallInsulated !== undefined && ext.isWallInsulated !== null ? `
                    <div style="background: white; padding: 6px 8px; border-radius: 4px; border-left: 3px solid ${ext.isWallInsulated ? '#10b981' : '#ef4444'};">
                      <div style="font-size: 8px; font-weight: 600; color: #6b7280; margin-bottom: 2px;">Wall Insulated</div>
                      <div style="font-size: 11px; font-weight: 600; color: ${ext.isWallInsulated ? '#059669' : '#dc2626'}; display: flex; align-items: center; gap: 4px;">
                        ${ext.isWallInsulated ? 
                          '<svg style="width: 12px; height: 12px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 12l2 2 4-4"></path><circle cx="12" cy="12" r="10"></circle></svg>' : 
                          '<svg style="width: 12px; height: 12px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>'
                        }
                        ${ext.isWallInsulated ? 'Yes' : 'No'}
                      </div>
                    </div>
                  ` : ''}
                </div>
              </div>
            ` : ''}
            
            ${hasRoofInfo ? `
              <div style="background: #fefce8; padding: 10px; border-radius: 6px; border: 1px solid #fde047; margin-bottom: 10px;">
                <div style="font-size: 9px; font-weight: 700; color: #713f12; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; display: flex; align-items: center; gap: 6px;">
                  <svg style="width: 12px; height: 12px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  Roof Information
                </div>
                <div style="background: white; padding: 6px 8px; border-radius: 4px;">
                  <div style="font-size: 8px; font-weight: 600; color: #6b7280; margin-bottom: 2px;">Roof Type</div>
                  <div style="font-size: 11px; font-weight: 600; color: #1f2937;">${formatRoofType(ext.roofType || '')}</div>
                </div>
              </div>
            ` : ''}
            
            ${ext.notes ? `
              <div style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 10px; border-radius: 6px; border-left: 4px solid #0284c7;">
                <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                  <svg style="width: 12px; height: 12px; color: #0369a1;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <span style="font-size: 9px; font-weight: 700; color: #0369a1; text-transform: uppercase; letter-spacing: 0.3px;">Additional Notes</span>
                </div>
                <div style="font-size: 9px; color: #0c4a6e; line-height: 1.5; padding-left: 18px;">${ext.notes}</div>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    });
  }

  html += `
      </div>
    </div>
  `;

  return html;
}

/**
 * Helper to format wall type
 */
function formatWallType(type: string): string {
  const typeMap: Record<string, string> = {
    'stone_granite_whinstone': 'Stone: granite or whinstone',
    'stone_sandstone_limestone': 'Stone: sandstone or limestone',
    'solid_brick': 'Solid Brick',
    'cob': 'Cob',
    'cavity': 'Cavity',
    'timber_frame': 'Timber Frame',
    'system_build': 'System Build',
    'curtain_wall': 'Curtain Wall'
  };
  return typeMap[type] || type;
}

/**
 * Helper to format roof type
 */
function formatRoofType(type: string): string {
  const typeMap: Record<string, string> = {
    'pitched': 'Pitched',
    'flat': 'Flat',
    'thatched': 'Thatched',
    'sloping': 'Sloping',
    'dwelling_above': 'Dwelling Above'
  };
  return typeMap[type] || type;
}

/**
 * Helper to capitalize words properly
 */
function capitalizeWords(text: string): string {
  if (!text) return '';
  
  // Handle special cases like "before-1900" or underscores
  return text
    .split(/[\s_-]+/)
    .map(word => {
      if (!word) return '';
      // Keep numbers as is
      if (/^\d+$/.test(word)) return word;
      // Capitalize first letter
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Generate Extensions Photo Pages (6 photos per page)
 */
export async function generateExtensionsPhotoPages(
  surveyData: RetrofitSurveyData,
  startPage: number,
  totalPages: number,
  options: { logoDataUrl?: string }
): Promise<string[]> {
  const { extensions } = surveyData;
  
  if (!extensions) {
    return [];
  }

  const pages: string[] = [];
  let allPhotos: { url: string; label: string }[] = [];

  // Collect conservatory photos with optimization
  if (extensions.hasConservatory && extensions.conservatoryPhotos) {
    const conservatoryPhotos = extensions.conservatoryPhotos;
    
    if (conservatoryPhotos.externalElevationImages?.length > 0) {
      for (const photo of conservatoryPhotos.externalElevationImages) {
        if (photo.url) {
          try {
            const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
            allPhotos.push({
              url: optimizedUrl,
              label: 'Conservatory - External Elevation'
            });
          } catch (error) {
            console.warn('Failed to optimize conservatory external photo, using original:', error);
            allPhotos.push({
              url: photo.url,
              label: 'Conservatory - External Elevation'
            });
          }
        }
      }
    }
    
    if (conservatoryPhotos.internalImages?.length > 0) {
      for (const photo of conservatoryPhotos.internalImages) {
        if (photo.url) {
          try {
            const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
            allPhotos.push({
              url: optimizedUrl,
              label: 'Conservatory - Internal'
            });
          } catch (error) {
            console.warn('Failed to optimize conservatory internal photo, using original:', error);
            allPhotos.push({
              url: photo.url,
              label: 'Conservatory - Internal'
            });
          }
        }
      }
    }
  }

  // Collect extension photos with optimization
  const existingExtensions = extensions.existingExtensions || [];
  for (const [index, ext] of existingExtensions.entries()) {
    const extName = ext.type || `Extension ${index + 1}`;
    
    if (ext.photos) {
      // Wall evidence photos
      if (ext.photos.wallEvidence?.length > 0) {
        for (const photo of ext.photos.wallEvidence) {
          if (photo.url) {
            try {
              const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
              allPhotos.push({
                url: optimizedUrl,
                label: `${extName} - Wall Evidence`
              });
            } catch (error) {
              console.warn(`Failed to optimize ${extName} wall photo, using original:`, error);
              allPhotos.push({
                url: photo.url,
                label: `${extName} - Wall Evidence`
              });
            }
          }
        }
      }
      
      // Wall thickness photos
      if (ext.photos.wallThickness?.length > 0) {
        for (const photo of ext.photos.wallThickness) {
          if (photo.url) {
            try {
              const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
              allPhotos.push({
                url: optimizedUrl,
                label: `${extName} - Wall Thickness`
              });
            } catch (error) {
              console.warn(`Failed to optimize ${extName} wall thickness photo, using original:`, error);
              allPhotos.push({
                url: photo.url,
                label: `${extName} - Wall Thickness`
              });
            }
          }
        }
      }
      
      // Roof type evidence photos
      if (ext.photos.roofTypeEvidence?.length > 0) {
        for (const photo of ext.photos.roofTypeEvidence) {
          if (photo.url) {
            try {
              const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
              allPhotos.push({
                url: optimizedUrl,
                label: `${extName} - Roof Evidence`
              });
            } catch (error) {
              console.warn(`Failed to optimize ${extName} roof photo, using original:`, error);
              allPhotos.push({
                url: photo.url,
                label: `${extName} - Roof Evidence`
              });
            }
          }
        }
      }
      
      // External elevation photos
      if (ext.photos.externalElevations?.length > 0) {
        for (const photo of ext.photos.externalElevations) {
          if (photo.url) {
            try {
              const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
              allPhotos.push({
                url: optimizedUrl,
                label: `${extName} - External Elevation`
              });
            } catch (error) {
              console.warn(`Failed to optimize ${extName} elevation photo, using original:`, error);
              allPhotos.push({
                url: photo.url,
                label: `${extName} - External Elevation`
              });
            }
          }
        }
      }
    }
  }

  console.log(`📸 [Extensions] Total photos collected: ${allPhotos.length}`);

  if (allPhotos.length === 0) {
    return [];
  }

  // Paginate photos (6 per page, matching other sections)
  const PHOTOS_PER_PAGE = 6;
  for (let i = 0; i < allPhotos.length; i += PHOTOS_PER_PAGE) {
    const pagePhotos = allPhotos.slice(i, i + PHOTOS_PER_PAGE);
    const pageNumber = Math.floor(i / PHOTOS_PER_PAGE);
    const totalPhotoPages = Math.ceil(allPhotos.length / PHOTOS_PER_PAGE);
    
    const photosHTML = pagePhotos.map(photo => `
      <div class="property-photo-wrapper">
        <div class="property-photo-item">
          <img src="${photo.url}" alt="${photo.label}" />
        </div>
        <div class="property-photo-label">${photo.label}</div>
      </div>
    `).join('');

    const pageHTML = `
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage + pageNumber, totalPages, options)}
        <div class="content-section">
          <div class="property-section">
            <div class="property-section-header">
              <div class="property-icon-container" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
                <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 3v18h18"></path>
                  <path d="M18 9l-5 5"></path>
                  <circle cx="9" cy="9" r="2"></circle>
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                </svg>
              </div>
              <h3><span class="section-parent">Extensions & Conservatory:</span> Photo Evidence${totalPhotoPages > 1 ? ` (${pageNumber + 1}/${totalPhotoPages})` : ''}</h3>
            </div>
            <div class="property-photo-grid">${photosHTML}</div>
          </div>
        </div>
        ${generateFooter(options)}
      </section>
    `;

    pages.push(pageHTML);
  }

  console.log(`📄 [Extensions] Generated ${pages.length} photo page(s) for ${allPhotos.length} photos`);

  return pages;
}

/**
 * Count total extension photos for page calculation
 */
export function getExtensionPhotoCount(extensions: any): number {
  if (!extensions) return 0;
  
  let count = 0;

  // Count conservatory photos
  if (extensions.hasConservatory && extensions.conservatoryPhotos) {
    const conservatoryPhotos = extensions.conservatoryPhotos;
    count += (conservatoryPhotos.externalElevationImages?.filter((p: any) => p.url).length || 0);
    count += (conservatoryPhotos.internalImages?.filter((p: any) => p.url).length || 0);
  }

  // Count extension photos
  const existingExtensions = extensions.existingExtensions || [];
  existingExtensions.forEach((ext: any) => {
    if (ext.photos) {
      count += (ext.photos.wallEvidence?.filter((p: any) => p.url).length || 0);
      count += (ext.photos.wallThickness?.filter((p: any) => p.url).length || 0);
      count += (ext.photos.roofTypeEvidence?.filter((p: any) => p.url).length || 0);
      count += (ext.photos.externalElevations?.filter((p: any) => p.url).length || 0);
    }
  });

  return count;
}
